// src/view/Project/DataRoom/FilePreview.tsx
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, Download as DownloadIcon } from '@mui/icons-material';

interface Props {
  open: boolean;
  url: string | null;
  name: string;
  contentType?: string | null;
  onClose: () => void;
}

// Infer a rough type from filename when content_type is missing.
const guessKind = (name: string, contentType?: string | null): 'image' | 'audio' | 'video' | 'pdf' | 'other' => {
  const ct = contentType || '';
  if (ct.startsWith('image/')) return 'image';
  if (ct.startsWith('audio/')) return 'audio';
  if (ct.startsWith('video/')) return 'video';
  if (ct === 'application/pdf') return 'pdf';
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) return 'image';
  if (['mp3', 'wav', 'ogg', 'm4a', 'flac', 'aac'].includes(ext)) return 'audio';
  if (['mp4', 'webm', 'mov'].includes(ext)) return 'video';
  if (ext === 'pdf') return 'pdf';
  return 'other';
};

const FilePreview = ({ open, url, name, contentType, onClose }: Props) => {
  const kind = guessKind(name, contentType);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography noWrap sx={{ pr: 2 }}>{name}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!url ? null : kind === 'image' ? (
          <Box sx={{ textAlign: 'center' }}>
            <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
          </Box>
        ) : kind === 'audio' ? (
          <Box sx={{ py: 4 }}>
            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
            <audio src={url} controls style={{ width: '100%' }} />
          </Box>
        ) : kind === 'video' ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video src={url} controls style={{ width: '100%', maxHeight: '70vh' }} />
        ) : kind === 'pdf' ? (
          <Box sx={{ height: '70vh' }}>
            <iframe src={url} title={name} style={{ width: '100%', height: '100%', border: 0 }} />
          </Box>
        ) : (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              This file type can’t be previewed.
            </Typography>
            <Button variant="contained" startIcon={<DownloadIcon />} href={url} target="_blank" rel="noopener">
              Download
            </Button>
          </Box>
        )}
        {url && kind !== 'other' && (
          <Box sx={{ textAlign: 'right', mt: 2 }}>
            <Button startIcon={<DownloadIcon />} href={url} target="_blank" rel="noopener">
              Download
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FilePreview;
